/**
 * Small custom elements used by the landing page sections and blocks.
 * No dependencies, safe to load multiple times.
 */

/**
 * Horizontal scroll track with previous / next buttons.
 * Markup: <lp-scroller><button data-scroll="prev"><button data-scroll="next"><div data-scroller-track>…
 */
class LpScroller extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-scroll]') : null;
    const track = this.querySelector('[data-scroller-track]');
    if (!button || !(track instanceof HTMLElement)) return;

    const firstItem = track.firstElementChild;
    const step = firstItem instanceof HTMLElement ? firstItem.offsetWidth + 14 : 260;
    const direction = button.getAttribute('data-scroll') === 'prev' ? -1 : 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({ left: step * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
  };
}

/**
 * Vertical customer video card with play / pause, mute toggle and a progress bar.
 */
class LpVideoCard extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.playButton = this.querySelector('[data-video-play]');
    this.muteButton = this.querySelector('[data-video-mute]');
    this.progress = this.querySelector('[data-video-progress]');

    if (!this.video) return;

    this.video.muted = true;
    this.video.playsInline = true;
    this.video.removeAttribute('autoplay');
    this.video.pause();

    this.playButton?.addEventListener('click', this.#togglePlay);
    this.muteButton?.addEventListener('click', this.#toggleMute);
    this.video.addEventListener('timeupdate', this.#onTimeUpdate);
    this.video.addEventListener('ended', this.#onEnded);
    this.video.addEventListener('play', this.#syncLabels);
    this.video.addEventListener('pause', this.#syncLabels);
  }

  disconnectedCallback() {
    this.playButton?.removeEventListener('click', this.#togglePlay);
    this.muteButton?.removeEventListener('click', this.#toggleMute);
    this.video?.removeEventListener('timeupdate', this.#onTimeUpdate);
    this.video?.removeEventListener('ended', this.#onEnded);
    this.video?.removeEventListener('play', this.#syncLabels);
    this.video?.removeEventListener('pause', this.#syncLabels);
  }

  #togglePlay = () => {
    if (!this.video) return;

    if (this.video.paused) {
      document.querySelectorAll('lp-video-card').forEach((card) => {
        if (card !== this && card instanceof LpVideoCard) card.pause();
      });
      this.video.play();
    } else {
      this.video.pause();
    }
  };

  pause() {
    this.video?.pause();
  }

  #toggleMute = () => {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.#syncLabels();
  };

  #onTimeUpdate = () => {
    if (!this.video || !(this.progress instanceof HTMLElement) || !this.video.duration) return;
    this.progress.style.width = `${(this.video.currentTime / this.video.duration) * 100}%`;
  };

  #onEnded = () => {
    if (this.progress instanceof HTMLElement) this.progress.style.width = '0%';
    if (this.video) this.video.currentTime = 0;
    this.#syncLabels();
  };

  #syncLabels = () => {
    if (!this.video) return;
    const playing = !this.video.paused;
    this.toggleAttribute('playing', playing);

    if (this.playButton) {
      const label = playing ? this.playButton.dataset.labelPause : this.playButton.dataset.labelPlay;
      this.playButton.textContent = label || '';
      this.playButton.setAttribute('aria-pressed', String(playing));
    }

    if (this.muteButton) {
      const label = this.video.muted ? this.muteButton.dataset.labelUnmute : this.muteButton.dataset.labelMute;
      this.muteButton.textContent = label || '';
      this.muteButton.setAttribute('aria-pressed', String(!this.video.muted));
    }
  };
}

/**
 * Filter tabs built from the items themselves. Items carry data-tab-tags (a handle)
 * and data-tab-label (the visible category name). The "all" tab shows the first
 * item of every category; a category tab shows all of its items.
 */
class LpTabs extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-tab-bar]');
    this.items = Array.from(this.querySelectorAll('[data-tab-tags]')).filter(
      (item) => item instanceof HTMLElement
    );

    const categories = new Map();
    for (const item of this.items) {
      const tag = item.dataset.tabTags;
      if (tag && !categories.has(tag)) categories.set(tag, item.dataset.tabLabel || tag);
    }

    if (this.bar instanceof HTMLElement && categories.size > 0 && !this.bar.querySelector('[data-tab-value]:not([data-tab-value="all"])')) {
      const template = this.bar.querySelector('[data-tab-value="all"]');
      for (const [tag, label] of categories) {
        if (!(template instanceof HTMLElement)) break;
        const button = /** @type {HTMLElement} */ (template.cloneNode(false));
        button.dataset.tabValue = tag;
        button.setAttribute('aria-pressed', 'false');
        button.textContent = label;
        this.bar.append(button);
      }
      this.bar.hidden = false;
    }

    this.addEventListener('click', this.#onClick);
    this.#apply('all');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-tab-value]') : null;
    if (!(button instanceof HTMLElement)) return;
    this.#apply(button.dataset.tabValue || 'all');
  };

  /** @param {string} value */
  #apply(value) {
    this.querySelectorAll('[data-tab-value]').forEach((tab) => {
      tab.setAttribute('aria-pressed', String(tab.getAttribute('data-tab-value') === value));
    });

    const seen = new Set();
    for (const item of this.items) {
      const tag = item.dataset.tabTags || '';
      if (value === 'all') {
        item.hidden = tag !== '' && seen.has(tag);
        seen.add(tag);
      } else {
        item.hidden = tag !== value;
      }
    }
  }
}

/**
 * Shows the first `data-visible` children of [data-show-more-grid] and reveals the rest
 * when the button is pressed. The button is removed when there is nothing to reveal.
 */
class LpShowMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-show-more]');
    this.wrapper = this.querySelector('[data-show-more-wrapper]');
    const grid = this.querySelector('[data-show-more-grid]');
    const visible = Number.parseInt(this.dataset.visible || '0', 10);
    const items = grid ? Array.from(grid.children).filter((child) => child instanceof HTMLElement) : [];

    if (!visible || items.length <= visible) {
      this.wrapper?.remove();
      return;
    }

    items.slice(visible).forEach((item) => {
      item.setAttribute('data-extra', '');
      item.hidden = true;
    });

    if (this.wrapper instanceof HTMLElement) this.wrapper.hidden = false;
    this.button?.addEventListener('click', this.#reveal);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#reveal);
  }

  #reveal = () => {
    this.querySelectorAll('[data-extra]').forEach((item) => {
      if (item instanceof HTMLElement) item.hidden = false;
    });
    this.wrapper?.remove();
  };
}

/**
 * Placeholder product gallery: arrows, counter and thumbnails switch the visible slide.
 */
class LpPlaceholderGallery extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-index]'));
    this.counter = this.querySelector('[data-counter]');
    this.index = 0;
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-step], [data-index]') : null;
    if (!(target instanceof HTMLElement) || !this.slides.length) return;

    const count = this.slides.length;
    const next = target.dataset.step
      ? (this.index + Number(target.dataset.step) + count) % count
      : Number(target.dataset.index);

    this.#show(next);
  };

  /** @param {number} index */
  #show(index) {
    this.index = index;
    this.slides.forEach((slide, i) => {
      if (slide instanceof HTMLElement) slide.hidden = i !== index;
    });
    this.thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
  }
}

/**
 * Replaces its own content with the matching element from the re-rendered product section
 * whenever the variant changes (Horizon dispatches shopify:product:select with a promise
 * that resolves to the new section HTML).
 */
class VariantLiveContent extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener('shopify:product:select', this.#onProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener('shopify:product:select', this.#onProductSelect);
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    if (!event.promise) return;

    event.promise
      .then((result) => {
        const html = result?.detail?.html;
        if (!html) return;

        const fresh = html.querySelector(`variant-live-content[data-live-id="${this.dataset.liveId}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

/**
 * Selects the bundle card configured in the bundle picker ("preselected package")
 * when the page was opened without a variant in the URL. Goes through Horizon's
 * own variant flow, so price, buy button and URL update as on a normal click.
 */
function preselectBundle() {
  document.querySelectorAll('variant-picker[data-preselect-index]').forEach((picker) => {
    if (!(picker instanceof HTMLElement) || picker.dataset.preselectDone) return;
    picker.dataset.preselectDone = 'true';

    const input = picker.querySelector(`input[data-input-index="${picker.dataset.preselectIndex}"]`);
    if (input instanceof HTMLInputElement && !input.checked && input.getAttribute('aria-disabled') !== 'true') {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

if (document.readyState === 'complete') {
  preselectBundle();
} else {
  window.addEventListener('load', preselectBundle, { once: true });
}

const definitions = {
  'lp-scroller': LpScroller,
  'lp-video-card': LpVideoCard,
  'lp-tabs': LpTabs,
  'lp-show-more': LpShowMore,
  'lp-placeholder-gallery': LpPlaceholderGallery,
  'variant-live-content': VariantLiveContent,
};

for (const [name, element] of Object.entries(definitions)) {
  if (!customElements.get(name)) customElements.define(name, element);
}
